<script lang="ts" setup>
    import { CallsToAction } from '~/data/CallsToAction'
    import { OffsetGroup } from '~/data/OffsetGroup'

    const slug = currentSlug()
    const { $preview } = useNuxtApp()
    const { data: project, refresh } =
        await useStatamicEntries().getEntryBySlug<Project>({
            collection: 'projects',
            value: slug,
        })

    useStatamicPreview(refresh)

    let callsToAction: CallsToAction

    if (!project.value?.inherit_ctas) {
        callsToAction = new CallsToAction(
            project.value?.calls_to_action ?? [],
            project.value?.images.length ?? 0,
        )
    } else {
        const { data: projectPage } = await useAsyncData(
            'entries/pages/projects',
            () =>
                getEntryBySlug<ProjectPage>({
                    collection: 'pages',
                    value: 'projects',
                }),
        )

        callsToAction = new CallsToAction(
            projectPage.value?.calls_to_action ?? [],
            project.value?.images.length ?? 0,
        )
    }

    const offsets = []

    if (project.value) {
        for (let i = 0; i < project.value.images.length; i++) {
            offsets.push(
                OffsetGroup.random(
                    callsToAction.callToActionAtIndex(i) !== null,
                ),
            )
        }
    }

    useHead({
        title: () => project.value?.title ?? 'Project',
        bodyAttrs: {
            class: {
                'project-single': true,
                [`project-${slug}`]: true,
                'with-feature-image': computed(
                    () => project.value?.feature_image != null,
                ),
            },
        },
    })

    const details = [
        { key: 'client', label: 'Client' },
        { key: 'location', label: 'Location' },
        { key: 'completion', label: 'Completion' },
        { key: 'duration', label: 'Duration' },
        { key: 'type', label: 'Type' },
        { key: 'detail', label: 'Detail' },
        { key: 'service_level', label: 'Service Level' },
    ]
</script>

<template>
    <div class="block-gap-b">
        <template v-if="project">
            <PageHeader
                :image="project.feature_image"
                :content="project.summary"
            >
                <NuxtLink to="/projects">
                    Back to
                    <span class="icon-arrow-northwest whitespace-nowrap">
                        Projects
                    </span>
                </NuxtLink>
            </PageHeader>

            <div class="gutter-x block-gap-b sm:layout-grid">
                <div
                    class="raw-content pl-0 text-[22px] sm:col-span-4 sm:col-start-1 sm:pr-4 md:col-span-9 md:col-start-2 lg:col-span-7 lg:col-start-3 xl:col-span-7 xl:col-start-3 2xl:col-span-5 2xl:col-start-4"
                    v-html="project.description"
                />

                <dl
                    class="mt-8 border-t border-grey pt-4 sm:col-span-2 sm:ml-4 sm:mt-0 md:col-span-4 md:col-start-12 lg:col-start-11 xl:col-span-3 xl:col-start-12 2xl:col-span-2 2xl:col-start-11"
                >
                    <template v-for="(detail, i) in details" :key="detail.key">
                        <template
                            v-if="
                                (detail.key == 'type' &&
                                    project.types.length > 0) ||
                                (detail.key != 'type' &&
                                    project.details[detail.key])
                            "
                        >
                            <dt
                                class="uppercase"
                                data-aos="fade-up"
                                :data-aos-delay="50 * i"
                                data-aos-offset="-100"
                            >
                                {{ detail.label }}
                            </dt>

                            <dd
                                class="mb-4 text-grey"
                                data-aos="fade-up"
                                :data-aos-delay="100 * i"
                                data-aos-offset="-100"
                            >
                                <template v-if="detail.key != 'type'">
                                    {{ project.details[detail.key] }}
                                </template>

                                <template v-else>
                                    {{
                                        project.types
                                            .map((item: Term) => item.title)
                                            .join(', ')
                                    }}
                                </template>
                            </dd>
                        </template>
                    </template>
                </dl>
            </div>

            <ul>
                <li
                    v-for="(image, i) in project.images"
                    class="layout-grid block-gap-y last:mb-0"
                    data-aos="fade-up"
                    data-aos-offset="-100"
                >
                    <Asset :asset="image" :class="offsets[i].classes">
                        <p
                            v-if="
                                image.title &&
                                image.title.indexOf('.jpg') === -1
                            "
                            class="uppercase"
                        >
                            {{ image.title }}
                        </p>

                        <template v-else-if="$preview">
                            <p v-if="!image.title" class="uppercase">
                                (No Image Title)
                            </p>
                            <p v-else class="uppercase">
                                (Image title contains file extension - this text
                                will be hidden outside of preview)
                            </p>
                        </template>

                        <p v-if="image.description" class="uppercase text-grey">
                            {{ image.description }}
                        </p>

                        <p v-else-if="$preview" class="uppercase">
                            (No Image Description)
                        </p>
                    </Asset>

                    <div
                        class="block-gap-t md:mt-0"
                        :class="offsets[i].complementary.classes"
                        v-if="callsToAction.callToActionAtIndex(i) !== null"
                    >
                        <div class="flex h-full items-center justify-center">
                            <CallToAction
                                :content="
                                    callsToAction.callToActionAtIndex(i)
                                        ?.content
                                "
                                :link="
                                    callsToAction.callToActionAtIndex(i)?.link
                                "
                                :linkLabel="
                                    callsToAction.callToActionAtIndex(i)
                                        ?.link_label
                                "
                            />
                        </div>
                    </div>
                </li>
            </ul>
        </template>
    </div>
</template>
